/** The approval question the card sends is form text: its headline, consequence lines and a footer for the form only. */
interface ApprovalWords {
    headline: string
    bullets: string[]
}

interface Segment {
    text: string
    bullet: boolean
}

/** The form's footer: it tells the user to tick a box, which the buttons already do. */
const FOOTER = /To approve|tick|accept\./i

const LOAD = /\bLoad\s*[“"]([^”"]+)[”"]/i
const TRUCK = /\btruck\s*[“"]([^”"]+)[”"]/i
const DRIVERS = /\bdrivers?\s*[“"]([^”"]+)[”"]/i

/** The text cut into its lines and its "…" separators, each without its bullet or quotes. */
const segmentsOf = (message: string): Segment[] =>
    message
        .split(/\n|…/)
        .map((raw) => ({
            text: raw
                .replace(/^\s*•\s*/, '')
                .replace(/[“”"]/g, '')
                .trim(),
            bullet: /^\s*•/.test(raw),
        }))
        .filter((segment) => segment.text.length > 0)

/**
 * The question as the user reads it: one headline ("Assign Truck 4131 · Andre Coleman to #2334765?") and the consequence lines. When the
 * names do not parse, the headline is the first line without quotes.
 */
export const approvalWordsOf = (message: string, method: 'assign' | 'unassign'): ApprovalWords => {
    const segments = segmentsOf(message)
    const header = segments[0] && !segments[0].bullet ? segments[0] : undefined
    const bullets = segments.filter((segment) => segment !== header && !FOOTER.test(segment.text)).map((segment) => segment.text)
    const load = LOAD.exec(message)?.[1]?.trim()
    const truck = TRUCK.exec(message)?.[1]?.trim()
    const drivers = DRIVERS.exec(message)?.[1]?.trim()
    if (load && truck) {
        const names = drivers ? ` · ${drivers}` : ''
        const headline = method === 'unassign' ? `Unassign Truck ${truck}${names} from #${load}?` : `Assign Truck ${truck}${names} to #${load}?`
        return { headline, bullets }
    }
    return { headline: header?.text ?? segments[0]?.text ?? '', bullets }
}
